import { Frame } from "@/models/frame";
import { Column } from "@/models/column";
import { debounce } from "@/assets/js/helpers";

export const charsets = [
	{
		charset: "Default Charset",
		collations: ["Default Collation"],
	},
	{
		charset: "utf8",
		collations: ["utf8_general_ci", "utf8_unicode_ci", "utf8_bin"],
	},
	{
		charset: "utf8mb4",
		collations: ["utf8mb4_general_ci", "utf8mb4_unicode_ci", "utf8mb4_bin"],
	},
	{
		charset: "latin1",
		collations: ["latin1_general_ci", "latin1_swedish_ci", "latin1_bin"],
	},
	{
		charset: "latin2",
		collations: ["latin2_general_ci", "latin2_croatian_ci", "latin2_bin"],
	},
	{
		charset: "ascii",
		collations: ["ascii_general_ci"],
	},
	// ...
] as const;

export const engines = [
	"InnoDB",
	"MyISAM",
	"ARCHIVE",
	"CVS",
	"BLACKHOLE",
] as const;

export type TCharset = (typeof charsets)[number]["charset"];
export type TCollation<C extends TCharset> = Extract<
	(typeof charsets)[number],
	{ charset: C }
>["collations"][number];

export class Table extends Frame {
	private _name: string;
	private _charset: TCharset = charsets[0].charset;
	private _collation: TCollation<TCharset> = charsets[0].collations[0];
	engine: string = engines[0];
	comment: string | null = null;
	columns: Column[] = [];
	readonly type: string;
	private colCounter: number = 1;
	private updateHeaderContentDebounce = debounce(
		() => this.updateHeaderContent(),
		100,
		false,
	);

	constructor(id: number, name?: string) {
		super(id);
		this.name = name || `table${this.id}`;
		this.type = "Table";
		// Init column
		const initColumn = this.newColumn();
		initColumn.primaryKey = true;
		initColumn.notNull = true;
		initColumn.dataType = "INT";
		initColumn.dataTypeLength = 11;
		this.minHeight = 45; // Header + column height + borders (top and bottom) - 1
	}

	// Name

	get name(): string {
		return this._name;
	}

	set name(name: string) {
		this._name = name;
		this.updateHeaderContentDebounce();
	}

	private updateHeaderContent() {
		const tableElement = document.getElementById(`item-${this.id}`);
		const headerContent = tableElement?.querySelector(
			".eer-table-header-content",
		);
		if (headerContent) {
			headerContent.textContent = this._name;
		}
	}

	// charset

	get charset(): string {
		return this._charset;
	}

	set charset(charset: TCharset) {
		const charsetExists = charsets.some((c) => c.charset === charset);

		if (charsetExists) {
			this._charset = charset;
		} else {
			this._charset = charsets[0].charset;
		}
	}

	// collation

	get collation(): string {
		return this._collation;
	}

	set collation(collation: TCollation<TCharset>) {
		if (!this._charset) return;

		const charsetObj = charsets.find(
			(c) => c.charset === this._charset,
		) as unknown as {
			charset: TCharset;
			collations: TCollation<TCharset>;
		};

		if (charsetObj && charsetObj.collations.includes(collation)) {
			this._collation = collation;
		} else {
			this._collation = charsets[0].collations[0];
		}
	}

	// New table

	placeNewItem(
		top: number,
		left: number,
		workspaceEl: HTMLElement,
		width?: number,
		height?: number,
	) {
		const itemElement = super.createNewItem(top, left, width, height);

		const tableElement = this.tableTemplate(itemElement);

		// Append the table element to the workspace
		workspaceEl.appendChild(tableElement);

		this.renderColumns();

		const workspaceRect = workspaceEl.getBoundingClientRect();
		this.workspaceOverflowCheck(workspaceRect, tableElement);
		this.activeItemClear();
	}

	private tableTemplate(tableElement: HTMLElement) {
		tableElement.classList.add("eer-table");

		// Create the header element
		const headerElement = document.createElement("div");
		headerElement.classList.add("item-header");
		headerElement.classList.add("eer-table-header");

		const headerContentElement = document.createElement("div");
		headerContentElement.classList.add("eer-table-header-content");

		headerContentElement.textContent = this.name;

		// Create the collapse icon element
		const collapseElement = document.createElement("div");
		collapseElement.classList.add("eer-table-header-caret-down");

		// Create column container
		const columnContainerElement = document.createElement("div");
		// columnContainerElement.classList.add("item-table-columns-container");
		columnContainerElement.classList.add("eer-table-columns-container");

		// Add click event to collapse element
		collapseElement.addEventListener("click", () => {
			this.isCollapsed = !this.isCollapsed; // Toggle the isCollapsed property

			const tableElement = document.getElementById(`item-${this.id}`);
			if (tableElement) {
				if (this.isCollapsed) {
					collapseElement.classList.add("eer-table-header-caret-right");
					this.previousHeight = this.height;
					this.height = 22;
					tableElement.style.height = `${this.height}px`;
					tableElement.classList.add("eer-table-collapsed");
				} else {
					collapseElement.classList.remove("eer-table-header-caret-right");
					this.height = this.previousHeight;
					tableElement.style.height = `${this.previousHeight}px`;
					tableElement.classList.remove("eer-table-collapsed");
				}
			}
		});

		headerElement.appendChild(headerContentElement);
		headerElement.appendChild(collapseElement);

		// Append the header to the table element
		tableElement.appendChild(headerElement);

		tableElement.appendChild(columnContainerElement);

		return tableElement;
	}

	// *********** Columns **********

	// Generate column name

	private generateColumnName(colCounter: number) {
		let name = `${this.name}_newcol${colCounter < 2 ? "" : colCounter}`;

		while (
			this.columns.some((existingColumn) => existingColumn.name === name)
		) {
			colCounter++;
			name = `${this.name}_newcol${colCounter}`;
		}

		return name;
	}

	// New column

	newColumn(): Column {
		const initColumnName = this.generateColumnName(this.colCounter);
		const columnInstance = new Column(initColumnName);
		this.columns.push(columnInstance);

		return columnInstance;
	}

	renderColumns() {
		const tableElement = document.getElementById(`item-${this.id}`);
		if (!tableElement) return;

		// Find the column container inside the table element
		const columnContainer = tableElement.querySelector(
			".eer-table-columns-container",
		);
		if (!columnContainer) return;

		// Clear the column container before rendering
		columnContainer.innerHTML = "";

		// Add all columns to the DOM to check their visibility
		const columnElements = this.columns
			.map((column) => {
				const columnElement = column.createColumnElement();
				if (columnElement) {
					columnContainer.appendChild(columnElement);
				}
				return columnElement;
			})
			.filter(Boolean) as HTMLElement[];

		const containerRect = columnContainer.getBoundingClientRect();

		// Get the number of fully visible columns
		const visibleCount = columnElements.filter((col) => {
			const colRect = col.getBoundingClientRect();
			return colRect.bottom <= containerRect.bottom;
		}).length;

		// Clear the container again and render only the fully visible columns
		columnContainer.innerHTML = "";
		this.columns.slice(0, visibleCount).forEach((column) => {
			const columnElement = column.createColumnElement();
			if (columnElement) {
				columnContainer.appendChild(columnElement);
			}
		});

		// Update the "Hidden Columns" element if there are hidden columns
		this.updateHiddenColumns(columnContainer, visibleCount);
	}

	private updateHiddenColumns(
		columnContainer: Element,
		visibleColumns: number,
	) {
		const totalColumns = this.columns.length;
		const hiddenColumns = Math.max(0, totalColumns - visibleColumns);

		// Check if a "Hidden Columns" element already exists
		const existingHiddenColumns = columnContainer.parentElement?.querySelector(
			".eer-table-columns-see-more",
		);

		if (hiddenColumns > 0) {
			// If there are hidden columns, update or create the "Hidden Columns" element
			if (!existingHiddenColumns) {
				const hiddenColumnsElement = document.createElement("div");
				hiddenColumnsElement.classList.add("eer-table-columns-see-more");
				hiddenColumnsElement.textContent = `${hiddenColumns} more...`;
				columnContainer.parentElement.appendChild(hiddenColumnsElement);
			} else {
				existingHiddenColumns.textContent = `${hiddenColumns} more...`;
			}

			columnContainer.classList.add("eer-table-columns-container-no-padding");
		} else if (existingHiddenColumns) {
			// If all columns are visible, remove the "Hidden Columns" element
			existingHiddenColumns.remove();

			columnContainer.classList.remove(
				"eer-table-columns-container-no-padding",
			);
		}
	}

	// Delete column

	deleteColumn(columnToDelete: Column) {
		// Find the index of the column to delete
		const index = this.columns.findIndex((column) => column === columnToDelete);

		if (index !== -1) {
			// Remove the column from the columns array
			this.columns.splice(index, 1);
		}

		this.renderColumns();
	}
}
